// xPixel.js — X (Twitter) conversion base code, loaded only after analytics
// consent (contract §3). This used to be an inline <script> in
// public/index.html that ran for every visitor before any consent decision.
//
// Grant: inject the standard `twq` stub + uwt.js once, then `config`.
// Withdraw: swap `window.twq` for a no-op so nothing on this page can call it.
// A script that already loaded cannot be unloaded until the page reloads; the
// consent gate keeps it from loading on the next page view.

const PIXEL_ID = 'rc5iy';
const SCRIPT_SRC = 'https://static.ads-twitter.com/uwt.js';

let injected = false;
let enabled = false;
let liveTwq = null;
let mutedTwq = null;

function makeMuted() {
  const muted = function mutedTwq() {};
  muted.version = '1.1';
  muted.queue = [];
  return muted;
}

export function loadXPixel(win = window, doc = document) {
  try {
    if (enabled) return; // idempotent grant
    enabled = true;
    if (injected) {
      // Re-grant in the same page view: restore the real function. If uwt.js
      // finished loading while muted it attached its executor to the muted
      // stub and never saw the live queue; carry the executor over and run
      // what was queued before the withdrawal ('config'). Same as the portal.
      if (liveTwq) {
        if (mutedTwq && mutedTwq.exe && !liveTwq.exe) {
          liveTwq.exe = mutedTwq.exe;
          const pending = Array.isArray(liveTwq.queue) ? liveTwq.queue.splice(0) : [];
          pending.forEach((args) => {
            try {
              liveTwq.exe.apply(liveTwq, Array.from(args));
            } catch {
              /* one bad call must not stop the rest */
            }
          });
        }
        win.twq = liveTwq;
      }
      return;
    }
    injected = true;
    if (!win.twq) {
      const s = function twq() {
        // eslint-disable-next-line prefer-rest-params
        if (s.exe) s.exe.apply(s, arguments); else s.queue.push(arguments);
      };
      s.version = '1.1';
      s.queue = [];
      win.twq = s;
    }
    liveTwq = win.twq;
    const script = doc.createElement('script');
    script.async = true;
    script.src = SCRIPT_SRC;
    (doc.head || doc.documentElement).appendChild(script);
    win.twq('config', PIXEL_ID);
  } catch {
    /* ad tag failures never affect the page */
  }
}

// First-party cookie uwt.js sets on the site's own domain. Withdrawal removes
// it (host and apex) along with muting twq. X's third-party cookies on its own
// domains are outside this page's reach.
const X_FIRST_PARTY_COOKIES = ['_twpid'];

export function clearXPixelCookies(win = typeof window !== 'undefined' ? window : null) {
  try {
    if (!win) return;
    const host = win.location.hostname;
    const domains = ['', '; domain=.coinversa.ai', `; domain=${host}`, `; domain=.${host}`];
    X_FIRST_PARTY_COOKIES.forEach((name) => {
      domains.forEach((domain) => {
        win.document.cookie = `${name}=; path=/; max-age=0${domain}`;
      });
    });
  } catch {
    /* cookies unavailable */
  }
}

export function disableXPixel(win = window) {
  try {
    enabled = false;
    if (!injected) return;
    if (win.twq && win.twq !== mutedTwq) liveTwq = win.twq;
    mutedTwq = mutedTwq || makeMuted();
    win.twq = mutedTwq;
    clearXPixelCookies(win);
  } catch {
    /* no-op */
  }
}

export function isXPixelEnabled() {
  return enabled;
}

export function __resetXPixelForTests() {
  injected = false;
  enabled = false;
  liveTwq = null;
  mutedTwq = null;
}
